import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { TUTORIALS, TUTORIAL_ORDER } from "@/lib/tutorials";
import { ZOOD_LOGO } from "@/lib/brand";
import { ArrowLeft, LifeBuoy } from "lucide-react";

export default function HelpPage() {
  const { user, role } = useAuth();
  const defaultRole = TUTORIAL_ORDER.includes(role) ? role : TUTORIAL_ORDER[0];
  const [active, setActive] = useState(defaultRole);
  const guide = TUTORIALS[active];

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      {!user && (
        <header className="border-b border-[#E5DECF] bg-[#FAF7F2]/85 backdrop-blur-md">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
            <Link to="/" data-testid="help-home-logo"><img src={ZOOD_LOGO} alt="ZoodLeap" className="h-9 w-auto" /></Link>
            <Link to="/" className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold text-[#0D9488] hover:bg-[#0D9488]/10" data-testid="help-back-home">
              <ArrowLeft size={16} /> Back to home
            </Link>
          </div>
        </header>
      )}

      <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6" data-testid="help-page">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#7A3FF2]">
          <LifeBuoy size={14} /> How to use ZoodLeap
        </div>
        <h1 className="mt-1 font-heading text-3xl sm:text-4xl font-extrabold text-[#1F2937]">Getting started guides</h1>
        <p className="mt-2 font-body text-[#6B7280]">Pick your role to see the key steps for your workflow.</p>

        <div className="mt-6 flex flex-wrap gap-2" data-testid="help-role-tabs">
          {TUTORIAL_ORDER.map((r) => {
            const T = TUTORIALS[r];
            const on = active === r;
            return (
              <button
                key={r}
                onClick={() => setActive(r)}
                data-testid={`help-tab-${r}`}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-colors ${on ? "text-white shadow-sm" : "bg-white border border-[#E5DECF] text-[#6B7280] hover:bg-[#F2ECE1]"}`}
                style={on ? { background: T.color } : {}}
              >
                <T.icon size={15} /> {T.label}
              </button>
            );
          })}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="mt-8"
            data-testid={`help-content-${active}`}
          >
            <div className="flex items-center gap-3 rounded-3xl border border-[#E5DECF] bg-white p-5">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl text-white" style={{ background: guide.color }}>
                <guide.icon size={24} />
              </div>
              <div>
                <div className="font-heading text-xl font-extrabold text-[#1F2937]">{guide.label}</div>
                <div className="font-body text-sm text-[#6B7280]">{guide.tagline}</div>
              </div>
            </div>

            <ol className="mt-5 space-y-3">
              {guide.steps.map((s, i) => (
                <motion.li
                  key={s.title}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="flex gap-4 rounded-3xl border border-[#E5DECF] bg-white p-5"
                  data-testid={`help-step-${active}-${i}`}
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl font-heading text-lg font-extrabold" style={{ background: `${guide.color}1A`, color: guide.color }}>
                    {i + 1}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <s.icon size={16} style={{ color: guide.color }} />
                      <h3 className="font-heading text-base font-bold text-[#1F2937]">{s.title}</h3>
                    </div>
                    <p className="mt-1 font-body text-sm text-[#6B7280]">{s.body}</p>
                  </div>
                </motion.li>
              ))}
            </ol>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
